import { useState, useEffect, Suspense } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Briefcase,
  Building,
  MapPin,
  Clock,
  Search,
  NotebookTabs,
  Link,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import StudentSidebar from "@/components/student-dashboard/components/StudentSidebar";

const api = axios.create({
  baseURL: 'https://localhost:7022',
  headers: {
    accept: "*/*",
    // Authorization: `Bearer ${token}`,
  },
});

interface Job {
  id: string;
  jobTitle: string;
  companyName: string;
  location: string;
  employerType: string;
  description: string;
  expirationDate: string;
  contactEmail: string;
  roleType: number;
  workplaceType: number;
  preferredCourses: [];
  preferredSkills: [];
  createdOn: string;
  updatedon: string;
}

export default function JobOpportunities() {
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [generalSearch, setGeneralSearch] = useState("");
  const [roleTypeFilter, setRoleTypeFilter] = useState<string>("all");
  const [workplaceTypeFilter, setWorkplaceTypeFilter] = useState<string>("all");
  const [locationSearch, setLocationSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const jobsPerPage = 6;

  useEffect(() => {
    const page = Number(searchParams.get("page")) || 1;
    setCurrentPage(page);
    fetchJobs(page);
  }, [searchParams]);

  const fetchJobs = async (page: number) => {
    try {
      const params: any = {};
      // General search using the new combined parameter
      if (generalSearch) {
        params.titleOrCompany = generalSearch;
      }
      if (locationSearch) params.location = locationSearch;
      if (workplaceTypeFilter !== "all") params.workplaceType = workplaceTypeFilter;
      if (roleTypeFilter !== "all") params.roleType = roleTypeFilter;

      const response = await api.get("/JobBoard/job-opportunities/search", { params });
      const data = response.data;

      const jobs: Job[] = (data.resultObject || []).map((item: any) => ({
        ...item,
      }));
      setJobs(jobs);
      if (data.total) {
        setTotalPages(Math.ceil(data.total / jobsPerPage));
      } else {
        setTotalPages(1);
      }
      console.log("Fetched jobs data:", data); // Debug log
    } catch (error) {
      toast.error("An error occurred while fetching jobs");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchJobs(1);
  };

  const handleApplyNow = (jobId: string) => {
    // Navigate to application page
    window.location.href = `/apply/${jobId}`;
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchJobs(1);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <StudentSidebar />
      <main className="flex-1 min-w-0">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <Suspense fallback={
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-bold text-gray-900">Available Jobs</h1>
                <p className="text-gray-600">Loading jobs...</p>
              </div>
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-2 text-gray-600">Please wait...</p>
              </div>
            </div>
          }>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-bold text-gray-900">Available Jobs</h1>
                <p className="text-gray-600">
                  Find your next opportunity from our list of available positions
                </p>
              </div>

              {/* Search Bar */}
              <div className="w-full max-w-md">
                <Input
                  type="text"
                  placeholder="Search job title or company name..."
                  value={generalSearch}
                  onChange={(e) => setGeneralSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                  className="w-full"
                />
              </div>

              {/* Filters Card */}
              <Card className="bg-white border border-gray-200 shadow-sm">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Role Type */}
                    <div className="flex flex-col gap-3">
                      <label className="text-sm font-medium text-gray-700">Role Type</label>
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant={roleTypeFilter === "1" ? "default" : "outline"}
                            onClick={() => setRoleTypeFilter("1")}
                            className={roleTypeFilter === "1" ? "bg-teal-600 hover:bg-teal-700" : ""}
                            size="sm"
                          >
                            Full Time
                          </Button>
                          <Button
                            variant={roleTypeFilter === "2" ? "default" : "outline"}
                            onClick={() => setRoleTypeFilter("2")}
                            className={roleTypeFilter === "2" ? "bg-teal-600 hover:bg-teal-700" : ""}
                            size="sm"
                          >
                            Part Time
                          </Button>
                          <Button
                            variant={roleTypeFilter === "3" ? "default" : "outline"}
                            onClick={() => setRoleTypeFilter("3")}
                            className={roleTypeFilter === "3" ? "bg-teal-600 hover:bg-teal-700" : ""}
                            size="sm"
                          >
                            Internship
                          </Button>
                          <Button
                            variant={roleTypeFilter === "all" ? "default" : "outline"}
                            onClick={() => setRoleTypeFilter("all")}
                            className={roleTypeFilter === "all" ? "bg-teal-600 hover:bg-teal-700" : ""}
                            size="sm"
                          >
                            All
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Workplace Type */}
                    <div className="flex flex-col gap-3">
                      <label className="text-sm font-medium text-gray-700">Workplace Type</label>
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant={workplaceTypeFilter === "1" ? "default" : "outline"}
                            onClick={() => setWorkplaceTypeFilter("1")}
                            className={workplaceTypeFilter === "1" ? "bg-teal-600 hover:bg-teal-700" : ""}
                            size="sm"
                          >
                            Onsite
                          </Button>
                          <Button
                            variant={workplaceTypeFilter === "2" ? "default" : "outline"}
                            onClick={() => setWorkplaceTypeFilter("2")}
                            className={workplaceTypeFilter === "2" ? "bg-teal-600 hover:bg-teal-700" : ""}
                            size="sm"
                          >
                            Remote
                          </Button>
                          <Button
                            variant={workplaceTypeFilter === "3" ? "default" : "outline"}
                            onClick={() => setWorkplaceTypeFilter("3")}
                            className={workplaceTypeFilter === "3" ? "bg-teal-600 hover:bg-teal-700" : ""}
                            size="sm"
                          >
                            Hybrid
                          </Button>
                          <Button
                            variant={workplaceTypeFilter === "all" ? "default" : "outline"}
                            onClick={() => setWorkplaceTypeFilter("all")}
                            className={workplaceTypeFilter === "all" ? "bg-teal-600 hover:bg-teal-700" : ""}
                            size="sm"
                          >
                            All
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="flex flex-col gap-3">
                      <label className="text-sm font-medium text-gray-700">Location</label>
                      <Input
                        type="text"
                        placeholder="e.g. Hanoi, Vietnam"
                        value={locationSearch}
                        onChange={(e) => setLocationSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                      />
                    </div>
                  </div>

                  {/* Apply Filters Button */}
                  <div className="flex justify-end mt-6">
                    <Button
                      onClick={handleApplyFilters}
                      className="bg-teal-600 hover:bg-teal-700 text-white px-8"
                    >
                      Apply Filters
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Jobs List */}
              {loading ? (
                <div className="space-y-4">
                  {/* Skeleton Header */}
                  <div className="flex items-center justify-between mb-4">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  {/* Skeleton Job Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <Card key={i} className="bg-white">
                        <CardHeader className="pb-4">
                          <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                            <div className="w-full">
                              <Skeleton className="h-5 w-full mb-2" />
                              <Skeleton className="h-4 w-3/4 mb-1" />
                              <Skeleton className="h-4 w-1/2 mb-1" />
                            </div>
                            <Skeleton className="h-6 w-20" style={{ borderRadius: '9999px' }} />
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <Skeleton className="h-4 w-full" />
                            <Skeleton className="h-4 w-2/3" />
                            <div className="flex items-center justify-between pt-2">
                              <Skeleton className="h-4 w-24" />
                              <Skeleton className="h-9 w-28" style={{ borderRadius: '6px' }} />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  {/* Skeleton Pagination */}
                  <div className="flex justify-center gap-2 mt-8">
                    <Skeleton className="h-10 w-24" style={{ borderRadius: '6px' }} />
                    <Skeleton className="h-10 w-10" style={{ borderRadius: '6px' }} />
                    <Skeleton className="h-10 w-10" style={{ borderRadius: '6px' }} />
                    <Skeleton className="h-10 w-10" style={{ borderRadius: '6px' }} />
                    <Skeleton className="h-10 w-20" style={{ borderRadius: '6px' }} />
                  </div>
                </div>
              ) : jobs ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {jobs.map((job) => (
                      <Card key={job.id} className="bg-white">
                        <CardHeader className="pb-4">
                          <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                            <div>
                              <CardTitle className="text-lg font-semibold">
                                {job.jobTitle}
                              </CardTitle>
                              <CardDescription className="flex items-center mt-1">
                                {job.companyName} - {job.location}
                              </CardDescription>
                              <CardDescription className="flex items-center mt-1">
                                {job.workplaceType === 1 ? "Onsite" : job.workplaceType === 2 ? "Remote" : "Hybrid"} - {job.roleType === 1 ? "Full Time" : job.roleType === 2 ? "Part Time" : "Internship"}
                              </CardDescription>
                            </div>
                            <Badge className="capitalize bg-gray-100 text-gray-800">
                              {job.employerType}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-base">
                            <div className="flex items-center text-gray-600">
                              Preferred Skills: {job.preferredSkills && job.preferredSkills.join(", ")}
                            </div>
                            <div className="flex items-center text-gray-600">
                              Deadline: {new Date(job.expirationDate).toLocaleDateString()}
                            </div>
                            <Button
                              className="w-fit mb-2"
                              style={{
                                backgroundColor: "#E1FFFC",
                                color: "#2D6F68",
                              }}
                              onClick={() => handleApplyNow(job.id)}>
                              View Details
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center gap-2 mt-8">
                      <Button
                        variant="outline"
                        disabled={currentPage === 1}
                        onClick={() => {
                          const newPage = currentPage - 1;
                          setCurrentPage(newPage);
                          fetchJobs(newPage);
                        }}
                      >
                        Previous
                      </Button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            onClick={() => {
                              setCurrentPage(page);
                              fetchJobs(page);
                            }}
                          >
                            {page}
                          </Button>
                        )
                      )}
                      <Button
                        variant="outline"
                        disabled={currentPage === totalPages}
                        onClick={() => {
                          const newPage = currentPage + 1;
                          setCurrentPage(newPage);
                          fetchJobs(newPage);
                        }}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No jobs found
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {generalSearch || locationSearch || roleTypeFilter !== "all" || workplaceTypeFilter !== "all"
                      ? "Try adjusting your search filters"
                      : "Check back later for new opportunities"}
                  </p>
                </div>
              )}
            </div>
          </Suspense>
        </div>
      </main>
    </div>
  );
}
